import * as React from 'react';
import { styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';
import { get, cloneDeep, sum, findIndex } from 'lodash';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CurrentUser from 'auth/currentUser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import generateNonce from 'utils/generateNonce';
import { useTranslation } from 'react-i18next';

interface ICountryOption {
  label: string;
  alpha3: string;
}

interface IColumn {
  field: string;
  headerLabel: string;
  width: number;
  type?: string;
}

const PREFIX = 'BulkEditableProductList';

const classes = {
  root: `${PREFIX}-root`,
  table: `${PREFIX}-table`,
  tableRow: `${PREFIX}-tableRow`,
  textField: `${PREFIX}-textField`,
};

const Root = styled('div')(() => ({
  [`&.${classes.root}`]: {},
  [`& .${classes.table}`]: {
    width: '100%',
    borderWidth: '1px',
    borderColor: '#e1e1e1',
    borderStyle: 'solid',
  },
  [`& .${classes.tableRow}`]: {
    '& > th': {
      padding: 10,
    },
    '& > td': {
      borderLeftWidth: '1px',
      borderLeftColor: '#e1e1e1',
      borderLeftStyle: 'solid',
      padding: 10,
    },
    padding: 0,
  },
  [`& .${classes.textField}`]: {
    float: 'right',
  },
}));

interface IEditableTableRowProps<TRow> {
  countries: ICountryOption[];
  index: number;
  initialRow: TRow;
  onAfterSelectedCountry: (code: string) => void;
  onCanEdit: (row: TRow) => boolean; // if this row can be editted
  onSubmit: (row: TRow, index: number) => Promise<void>;
  onCancel: () => void;
  onDelete?: (row: TRow) => Promise<void>;
}

function generateColumns(t: (key: string) => string): IColumn[] {
  const columns: IColumn[] = [
    { field: 'companyName', headerLabel: t('Company Name'), width: 5 },
    {
      field: 'countryCode',
      headerLabel: t('country'),
      width: 2,
      type: 'country',
    },
    {
      field: 'productDescription',
      headerLabel: t('Product Description'),
      width: 5,
    },
    {
      field: 'certificates',
      headerLabel: t('Certificates'),
      width: 3,
      type: 'textList',
    },
    { field: 'transactionId', headerLabel: t('Transaction ID'), width: 2 },
  ];
  return columns;
}
function EditableTableRow<TRow>(props: IEditableTableRowProps<TRow>) {
  const { t } = useTranslation('default');

  const columns = generateColumns(t);

  const {
    initialRow,
    index,
    countries,
    onAfterSelectedCountry,
    onDelete,
    onSubmit,
    onCancel,
    // onCanEdit,
  } = props;
  const [editMode, setEditMode] = React.useState(false);
  const [row, setRow] = React.useState<TRow>(initialRow);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!get(initialRow, 'id')) {
      setEditMode(true);
    }
  }, [initialRow]);

  const handleChangeCell = (name: string, value: string) => {
    const newRow = cloneDeep(row);
    newRow[name] = value;
    setRow(newRow);
  };

  const handleChangeCountryCell = (name: string, value: string) => {
    onAfterSelectedCountry(value);
    handleChangeCell(name, value);
  };

  const renderTableCell = (column: IColumn) => (
    <TableCell key={column.field} align="left" component="td">
      {renderInput(column)}
    </TableCell>
  );

  // const handleEdit = () => {
  //   setEditMode(true);
  // };

  const handleCancel = () => {
    setRow(initialRow);
    setEditMode(false);
    onCancel();
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSubmit(row, index);
      setEditMode(false);
    } catch (e) {
      console.log(e);
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await onDelete?.(row);
    } catch (e) {
      console.log(e);
    }
    setLoading(false);
  };

  const renderInput = (column: IColumn) => {
    const { field, type } = column;
    const fieldValue = row[field];
    if (type === 'country') {
      const alpha3 = fieldValue || '';
      const label = countries.find((o) => o.alpha3 === fieldValue || '')?.label || '';
      const value = alpha3 ? { alpha3, label } : null;
      if (!editMode) {
        return label;
      }
      return (
        <Autocomplete
          size="small"
          disabled={countries.length < 1}
          id=""
          value={value}
          onChange={(e, newValue: ICountryOption | null) =>
            handleChangeCountryCell(field, newValue?.alpha3 || '')
          }
          options={countries}
          sx={{ minWidth: 200 }}
          renderInput={(params: any) => (
            <TextField
              {...params}
              label={t('select-a-country')}
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
      );
    } else if (type === 'textList') {
      const list = Array.isArray(fieldValue) ? fieldValue : [];
      if (!editMode) {
        return (list || []).join(', ');
      }
      return null;
    } else {
      if (!editMode) {
        return fieldValue;
      }
      return (
        <TextField
          size="small"
          sx={{ width: '100%' }}
          value={fieldValue || ''}
          name={field}
          onChange={(e) => handleChangeCell(field, e.target.value)}
          placeholder=""
          inputProps={{ 'aria-label': 'description' }}
        />
      );
    }
  };

  const renderReadViewButtons = () => {
    // const editable = onCanEdit(initialRow);
    return (
      <Stack direction="row" spacing={2}>
        <IconButton
          color="primary"
          aria-label="delete item"
          component="span"
          onClick={() => handleDelete()}
        >
          {loading ? (
            <CircularProgress color="error" size={20} />
          ) : (
            <DeleteForeverIcon color="error" />
          )}
        </IconButton>
      </Stack>
    );
  };

  const renderButtons = () => {
    if (editMode) {
      return (
        <Stack direction="row" spacing={2}>
          <LoadingButton
            size="small"
            onClick={handleSave}
            loading={loading}
            loadingPosition="start"
            startIcon={<SaveIcon />}
            variant="contained"
          >
            {t('save')}
          </LoadingButton>
          <IconButton color="primary" aria-label="cancel" component="span" onClick={handleCancel}>
            <CancelIcon />
          </IconButton>
        </Stack>
      );
    } else {
      return renderReadViewButtons();
    }
  };

  return (
    <TableRow className={classes.tableRow}>
      <TableCell align="left" component="td">
        {index + 1}
      </TableCell>
      {columns.map((c) => renderTableCell(c))}
      <TableCell align="left" component="td">
        {renderButtons()}
      </TableCell>
    </TableRow>
  );
}

interface IProps<TRow> {
  countries: ICountryOption[];
  list?: TRow[];
  onAdd?: () => void;
  onCsvUpload: () => void;
  onCanEdit: (row: TRow) => boolean;
  onSubmit?: (row: TRow) => Promise<TRow | undefined>;
  onDelete?: (row: TRow) => Promise<void>;
}

export default function BulkEditableProductList<TRow>(props: IProps<TRow>) {
  const { countries, list = [], onCsvUpload, onAdd, onCanEdit, onDelete, onSubmit } = props;
  // const defaultCountry = CurrentUser.getInstance().getScope('country') || '';
  const { t } = useTranslation('default');
  const columns = generateColumns(t);

  const handleSelectDefaultCountry = (countryCode: string) => {
    CurrentUser.getInstance().setScope('country', countryCode);
  };

  const [rows, setRows] = React.useState<TRow[]>([]);

  React.useEffect(() => {
    setRows(list);
  }, [list]);

  const handleDeleteRow = async (row: TRow) => {
    await onDelete?.(row);

    setRows((oldRows) => {
      const newRows = cloneDeep(oldRows);
      const index = findIndex(newRows, (i) => get(i, 'id') === get(row, 'id'));
      if (index >= 0) {
        newRows.splice(index, 1);
      }
      return newRows;
    });
  };

  const handleSubmit = async (row: TRow, index: number) => {
    const createOrUpdate = await onSubmit?.(row);
    if (createOrUpdate) {
      const newRows = [...rows];
      newRows[index] = createOrUpdate;
      setRows(newRows);
    }
  };

  const renderTableHeader = () => {
    const indexWidth = 1;
    const actionWdith = 2;
    const total = sum(columns.map((c) => c.width)) + indexWidth + actionWdith;
    return (
      <TableHead>
        <TableRow className={classes.tableRow}>
          <TableCell style={{ width: `${(indexWidth * 100) / total}%` }}>#</TableCell>
          {columns.map((c, index) => {
            const width = (c.width / total) * 100;
            return (
              <TableCell key={index} style={{ width: `${width}%` }} align="left">
                {c.headerLabel}
              </TableCell>
            );
          })}
          <TableCell style={{ width: `${(actionWdith * 100) / total}%` }}>{t('actions')}</TableCell>
        </TableRow>
      </TableHead>
    );
  };

  const handleCancel = () => {
    const newRows = rows.filter((r) => get(r, 'id'));
    setRows(newRows);
  };

  const renderTable = () => {
    return (
      <TableContainer className={classes.table} component={'div'} sx={{ maxHeight: 1000 }}>
        <Table stickyHeader className={classes.table} aria-label="simple table">
          {renderTableHeader()}
          <TableBody>
            {rows.map((r, index) => (
              <EditableTableRow
                initialRow={r}
                countries={countries}
                key={generateNonce()} // do not use index, otherwise when render different page the old old row will be cached
                index={index}
                onAfterSelectedCountry={handleSelectDefaultCountry}
                onCanEdit={onCanEdit}
                onDelete={handleDeleteRow}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderTopButtons = () => {
    return (
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button onClick={onAdd} variant="contained" startIcon={<ListAltIcon />}>
          {t('Add new record')}
        </Button>
        <Button onClick={onCsvUpload} variant="contained" startIcon={<ListAltIcon />}>
          {t('csv-upload')}
        </Button>
      </Stack>
    );
  };

  return (
    <Root className={classes.root}>
      <form noValidate autoComplete="off">
        <Stack spacing={2}>
          {renderTopButtons()}
          {renderTable()}
        </Stack>
      </form>
    </Root>
  );
}
